import { motion } from 'framer-motion';
import { SceneFrame, Pill } from './_atoms';

const catalog = [
  { cat: 'Provider',  tone: 'teal',    roles: ['Admin', 'Clinician', 'Billing Clerk', 'Auditor'],    scope: 'Own entities only' },
  { cat: 'Payer',     tone: 'amber',   roles: ['Admin', 'Reviewer', 'Adjudicator', 'Finance'],        scope: 'Own entities only' },
  { cat: 'Regulator', tone: 'indigo',  roles: ['Inspector', 'Analyst', 'Senior Regulator'],            scope: 'All entities — read' },
  { cat: 'Owner',     tone: 'rose',    roles: ['Platform Admin', 'Owner Analyst'],                    scope: 'All entities — full' },
];

export default function ArchRolesScene() {
  return (
    <SceneFrame
      eyebrow="Role management"
      title="A catalog per category"
      subtitle="Roles are not global — they derive their meaning from the category that defines them. Roles from one category are not portable to another, preventing role explosion and semantic misuse."
    >
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
        className="rounded-2xl bg-white border border-[#E5E7EB] overflow-hidden shadow-sm"
      >
        <div className="grid grid-cols-[180px_1fr_180px] gap-0 bg-[#FAFAFA] border-b border-[#E5E7EB] px-6 py-3">
          <span className="text-[11px] font-body font-bold text-[#374151] uppercase tracking-wider">Entity category</span>
          <span className="text-[11px] font-body font-bold text-[#374151] uppercase tracking-wider">Example roles</span>
          <span className="text-[11px] font-body font-bold text-[#374151] uppercase tracking-wider text-right">Access scope</span>
        </div>

        {catalog.map((row, i) => (
          <motion.div
            key={row.cat}
            initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 + i * 0.18 }}
            className="grid grid-cols-[180px_1fr_180px] gap-0 px-6 py-5 border-b border-[#F3F4F6] last:border-0 items-center"
          >
            <div>
              <Pill tone={row.tone}>{row.cat}</Pill>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {row.roles.map((r, j) => (
                <motion.span
                  key={r}
                  initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 + i * 0.18 + 0.1 + j * 0.05 }}
                  className={`px-2.5 py-1 rounded-md border text-[11.5px] font-body font-medium bg-white ${
                    row.tone === 'teal'    ? 'text-teal-700 border-teal-200' :
                    row.tone === 'amber'   ? 'text-amber-800 border-amber-200' :
                    row.tone === 'indigo'  ? 'text-indigo-700 border-indigo-200' :
                                             'text-rose-700 border-rose-200'
                  }`}
                >{r}</motion.span>
              ))}
            </div>
            <div className="text-right text-[12px] font-body text-[#6B7280]">
              {row.scope}
            </div>
          </motion.div>
        ))}
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8 }}
        className="mt-5 rounded-xl bg-gradient-to-r from-teal-50 via-white to-amber-50 border border-teal-600/20 px-4 py-3 max-w-4xl"
      >
        <p className="text-[12.5px] font-body text-[#374151] leading-relaxed">
          <span className="font-bold text-teal-700">Role assignment is per-entity, not per-user.</span>{' '}
          One user assigned to 3 Provider entities might hold <em>Admin</em> in entity A, <em>Billing Clerk</em> in B, and <em>Auditor</em> in C — all drawn from the Provider catalog.
        </p>
      </motion.div>
    </SceneFrame>
  );
}
